import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, ArrowLeft, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

type LinkInfo = { purpose: "INVITE" | "RESET"; email: string; first_name?: string; expires_at: string; min_length: number };

/** Reads the link secret once from the address bar (#token=…) and removes it, so it stays out of history */
function takeTokenFromUrl(): string {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = params.get("token") || "";
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

/**
 * Where invite and password-reset links land. The link is checked first (who it is for, still valid?),
 * then the user chooses their password; the link stops working once used.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useToast();
  const [token] = useState(takeTokenFromUrl);
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [linkError, setLinkError] = useState<string | null>(token ? null : "This link is incomplete. Open it again from your email.");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .post("/api/auth/password-link/check", { token })
      .then((res) => setInfo(res as LinkInfo))
      .catch((err: any) => setLinkError(err.message || "This link is invalid or has expired."));
  }, [token]);

  const minLength = info?.min_length || 8;
  const isInvite = info?.purpose === "INVITE";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < minLength) {
      toastError("Password too short", `Your password must be at least ${minLength} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      toastError("Passwords do not match", "Type the same password in both fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/password-link/set", { token, password });
      toastSuccess("Password saved", "You can now sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err: any) {
      toastError("Could not save password", err.message || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-slate-100">
      <Card className="w-full max-w-md shadow-xl bg-white border-slate-200">
        <CardHeader className="space-y-1">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2 transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Back to login
          </Link>
          <CardTitle className="text-xl font-bold">
            {linkError ? "Link not valid" : isInvite ? "Welcome! Choose your password" : "Choose a new password"}
          </CardTitle>
          {info && (
            <CardDescription>
              {isInvite ? `Hi ${info.first_name || ""}, set the password` : "New password"} for{" "}
              <span className="font-medium text-foreground">{info.email}</span>. This link works once.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {linkError ? (
            <div className="space-y-4">
              <div className="flex gap-2 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-200">
                <AlertTriangle className="size-4 shrink-0" />
                <span>{linkError} Invite links last 72 hours and reset links 30 minutes; each works only once.</span>
              </div>
              <Link to="/forgot-password">
                <Button className="w-full h-10">Send me a new reset link</Button>
              </Link>
              <p className="text-[11px] text-muted-foreground text-center">
                New to the CRM? Ask your administrator to resend your invite.
              </p>
            </div>
          ) : !info ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-6 animate-spin text-primary" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Lets password managers save the new password against the right account */}
              <input type="text" name="username" autoComplete="username" value={info.email} readOnly hidden />
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold">
                  {isInvite ? "Password" : "New password"}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 h-10 text-sm"
                    maxLength={72}
                    autoFocus
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-xs font-semibold">
                  Confirm password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-9 h-10 text-sm"
                    maxLength={72}
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                <ShieldCheck className="size-4 shrink-0 text-primary" />
                <span>
                  At least {minLength} characters. A long phrase is easiest to remember. Common or previously leaked
                  passwords, and your name or email, are not accepted.
                </span>
              </div>
              <Button type="submit" className="w-full h-10" disabled={submitting}>
                {submitting ? <Loader2 className="size-4 animate-spin" /> : isInvite ? "Set password" : "Save new password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
